import { useCallback, useEffect, useRef, useState } from 'react'

const NONE = {}

export const jsonEquals = (a, b) => JSON.stringify(a) === JSON.stringify(b)

/**
 * Optimistic value for controls that save as soon as they change (toggles).
 * A change shows at once; saves run one at a time and the latest wins, so
 * quick repeated clicks all count and the server ends up with the value on
 * screen. `stored` only resyncs the local value while nothing is being saved,
 * so a save of its own can never flip the control back. A failed save
 * reverts to the last saved value.
 *
 * @param {any} stored the saved value
 * @param {(value: any) => Promise<any>} persist saves a value
 * @param {(a: any, b: any) => boolean} [equals]
 * @returns {[any, (value: any) => void]}
 */
const useQueuedSave = (stored, persist, equals = Object.is) => {
  const [value, setValue] = useState(stored)
  const saved = useRef(stored)
  const busy = useRef(false)
  const pending = useRef(NONE)
  const persistRef = useRef(persist)
  persistRef.current = persist
  const equalsRef = useRef(equals)
  equalsRef.current = equals

  useEffect(() => {
    if (busy.current || equalsRef.current(stored, saved.current)) return
    saved.current = stored
    setValue(stored)
  }, [stored])

  const send = useCallback(next => {
    busy.current = true
    Promise.resolve()
      .then(() => persistRef.current(next))
      .then(
        () => {
          saved.current = next
          busy.current = false
          const queued = pending.current
          pending.current = NONE
          if (queued !== NONE && !equalsRef.current(queued, next)) send(queued)
        },
        () => {
          busy.current = false
          pending.current = NONE
          setValue(saved.current)
        },
      )
  }, [])

  const update = useCallback(
    next => {
      setValue(next)
      if (busy.current) pending.current = next
      else if (!equalsRef.current(next, saved.current)) send(next)
    },
    [send],
  )

  return [value, update]
}

export default useQueuedSave
